/**
 * MigrationDialog — single-plan one-click migration UX.
 *
 * Flow:
 *   1) Open dialog → fetch /migrate/preview.
 *   2) Show:
 *      - candidate row/section stats
 *      - legacy → section mapping
 *      - any "unmapped" rows that landed in About Me
 *      - conflict banner when the plan already has user-authored
 *        structured data (origin != 'migration').
 *   3) "Apply migration" calls /migrate/apply with `confirm=true`.
 *      Conflicts require explicit `overwrite_user_authored=true`.
 *   4) "Undo migration" calls /migrate/undo (only enabled when the
 *      plan currently carries migration-origin structured data).
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, Undo2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export function MigrationDialog({ planId, onClose, onComplete }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  useEffect(() => {
    api.post(`/careplans/${planId}/migrate/preview`)
      .then((r) => setPreview(r.data.data))
      .catch(() => toast.error('Failed to load migration preview'))
      .finally(() => setLoading(false));
  }, [planId]);

  const apply = async () => {
    setApplying(true);
    try {
      await api.post(`/careplans/${planId}/migrate/apply`, {
        confirm: true,
        overwrite_user_authored: confirmOverwrite,
      });
      toast.success('Migration applied — structured rows populated');
      onComplete?.();
    } catch (e) {
      const msg = e?.response?.data?.error?.message || 'Migration failed';
      toast.error(msg);
    } finally { setApplying(false); }
  };

  const undo = async () => {
    setApplying(true);
    try {
      await api.post(`/careplans/${planId}/migrate/undo`);
      toast.success('Migration undone — structured rows removed');
      onComplete?.();
    } catch (e) {
      const msg = e?.response?.data?.error?.message || 'Undo failed';
      toast.error(msg);
    } finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="structured-migration-dialog">
      <Card className="w-[720px] max-w-full max-h-[90vh] overflow-y-auto">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Migrate legacy fields → structured matrix</h3>
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Computing preview…
            </div>
          ) : !preview ? (
            <p className="text-sm text-muted-foreground">Could not load preview.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline" className="bg-zinc-50 text-zinc-700 border-zinc-200">
                  Will create {preview.candidate_stats?.rows ?? 0} rows across {preview.candidate_stats?.sections_populated ?? 0} sections
                </Badge>
                {preview.unmapped_count > 0 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    {preview.unmapped_count} unmapped → About Me
                  </Badge>
                )}
                {preview.has_existing_structured && (
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                    existing structured data ({preview.existing_origin})
                  </Badge>
                )}
              </div>

              {preview.will_conflict && (
                <div className="flex items-start gap-2 p-3 border border-rose-300 bg-rose-50 rounded text-sm" data-testid="structured-migration-conflict">
                  <AlertTriangle className="h-4 w-4 text-rose-700 mt-0.5" />
                  <div>
                    <p className="font-semibold text-rose-800">
                      User-authored structured data already exists on this plan.
                    </p>
                    <p className="text-xs text-rose-700 mt-1">
                      Applying migration will replace ALL existing structured rows.
                      Tick the box below if you really want to proceed.
                    </p>
                    <label className="text-xs flex items-center gap-2 mt-2">
                      <input type="checkbox"
                             checked={confirmOverwrite}
                             onChange={(e) => setConfirmOverwrite(e.target.checked)}
                             data-testid="structured-migration-overwrite-confirm" />
                      I understand — overwrite the existing user-authored structured rows.
                    </label>
                  </div>
                </div>
              )}

              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Mapping (legacy fields → structured rows)
                </p>
                <ul className="space-y-1">
                  {preview.mapping.map((m, i) => (
                    <li key={i} className="text-xs flex items-start gap-2 border rounded px-2 py-1.5">
                      <Badge variant="outline" className="text-[10px]">{m.section_key}</Badge>
                      <ArrowRight className="h-3 w-3 mt-0.5 opacity-60" />
                      <span className="flex-1">
                        <span className="font-medium">{m.observation_or_need}</span>
                        <span className="text-muted-foreground ml-2">{m.intervention_preview}</span>
                        {m.is_legacy_unmapped && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] ml-2">
                            unmapped legacy: {m.legacy_section_key}
                          </Badge>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                {preview.mapping.length === 0 && (
                  <p className="text-xs italic text-muted-foreground">
                    No legacy content to migrate — this plan has nothing to map.
                  </p>
                )}
              </div>

              <div className="flex justify-between pt-3 border-t">
                <Button size="sm" variant="outline"
                        onClick={undo}
                        disabled={applying || !preview.has_existing_structured || preview.existing_origin !== 'migration'}
                        data-testid="structured-migration-undo">
                  <Undo2 className="h-3 w-3 mr-1" /> Undo previous migration
                </Button>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={onClose} disabled={applying}>Cancel</Button>
                  <Button size="sm"
                          onClick={apply}
                          disabled={applying || (preview.will_conflict && !confirmOverwrite) || preview.mapping.length === 0}
                          data-testid="structured-migration-apply">
                    {applying ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Apply Migration
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default MigrationDialog;
